import React, { useEffect, useState, useCallback } from "react";
import { FaSearch, FaSync, FaEdit, FaCoins, FaTimes, FaUsers, FaUserCheck } from "react-icons/fa";
import { imPointsApiService } from "../../../service/admin/imPointsApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import "./style.css";

const EXPIRY_OPTIONS = [
  { label: "30 days", value: 30 },
  { label: "50 days", value: 50 },
  { label: "60 days", value: 60 },
  { label: "90 days", value: 90 },
  { label: "Custom", value: "custom" },
];

const defaultForm = {
  points: "",
  percentage: "",
  expiryDays: 30,
  customDays: "",
  password: "",
  scope: "selected", // 'selected' | 'all'
};

/** Backend returns _id; legacy credit list used userId */
function getUserRowId(user) {
  if (!user) return null;
  return user._id || user.userId || null;
}

export default function IMPointsAssign() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [limit, setLimit] = useState(15);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null); // null = new assign, object = edit
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);

  const message = useMessage();

  const buildQuery = useCallback(() => {
    return `?search=${search}&page=${page}&limit=${limit}`;
  }, [search, page, limit]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    imPointsApiService
      .getIMPointsUsers(buildQuery())
      .then((res) => {
        if (res?.status?.code === 200) {
          setUsers(res.data?.users || []);
          setPages({
            currentPage: res.data?.currentPage || 1,
            totalPages: res.data?.totalPages || 1,
            totalItems: res.data?.totalItems || 0,
          });
        }
      })
      .catch(() => message.showToast("Failed to load users", ToastTypes.ERROR))
      .finally(() => setLoading(false));
  }, [buildQuery]);

  useEffect(() => {
    fetchUsers();
  }, [page, limit]);

  // Only fetch when search is submitted, not on every keystroke

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(users.map((u) => u._id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openAssignModal = (scope = "selected", user = null) => {
    setEditUser(user);
    setForm({
      ...defaultForm,
      scope: user ? "selected" : scope,
      points: user?.IM_Points?.points || "",
      percentage: user?.IM_Points?.percentage || "",
      expiryDays: user?.IM_Points?.expiryDays || 30,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    setEditUser(null);
    setForm(defaultForm);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const getEffectiveExpiryDays = () => {
    if (form.expiryDays === "custom") {
      return parseInt(form.customDays, 10) || 0;
    }
    return parseInt(form.expiryDays, 10);
  };

  const handleSubmit = async () => {
    if (!form.points || !form.percentage) {
      message.showToast("Points and percentage are required", ToastTypes.ERROR);
      return;
    }
    if (!form.password) {
      message.showToast("Admin password is required", ToastTypes.ERROR);
      return;
    }
    const expiryDays = getEffectiveExpiryDays();
    if (!expiryDays || expiryDays < 1) {
      message.showToast("Please set a valid expiry period", ToastTypes.ERROR);
      return;
    }

    const payload = {
      points: parseFloat(form.points),
      percentage: parseFloat(form.percentage),
      expiryDays,
      password: form.password,
    };

    const editId = editUser ? getUserRowId(editUser) : null;
    if (editUser) {
      if (!editId) {
        message.showToast("Invalid user id for update", ToastTypes.ERROR);
        return;
      }
      payload.userIds = [editId];
    } else if (form.scope === "all") {
      payload.applyToAll = true;
    } else {
      if (!selectedIds.length) {
        message.showToast("Select at least one user", ToastTypes.ERROR);
        return;
      }
      payload.userIds = selectedIds;
    }

    setSubmitting(true);
    try {
      const fn = editUser
        ? imPointsApiService.updateIMPoints(editId, payload)
        : imPointsApiService.assignIMPoints(payload);
      const res = await fn;
      if (res?.status?.code === 200 || res?.status?.code === 201) {
        message.showToast(
          editUser ? "IM Points updated successfully" : "IM Points assigned successfully",
          ToastTypes.SUCCESS
        );
        closeModal();
        setSelectedIds([]);
        fetchUsers();
      } else {
        message.showToast(res?.status?.message || "Operation failed", ToastTypes.ERROR);
      }
    } catch (err) {
      const msg =
        err?.data?.status?.message ||
        err?.message ||
        err?.data?.message ||
        "Something went wrong";
      message.showToast(msg, ToastTypes.ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected =
    users.length > 0 &&
    users.every((u) => selectedIds.includes(getUserRowId(u)));
  const someSelected = selectedIds.length > 0;

  const renderPageButtons = () => {
    const btns = [];
    const { currentPage, totalPages } = pages;
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    if (start > 1) {
      btns.push(
        <button key={1} className="imp-page-btn" onClick={() => setPage(1)}>1</button>
      );
      if (start > 2) btns.push(<span key="s1" style={{ fontSize: 10, color: "#9ca3af" }}>…</span>);
    }
    for (let i = start; i <= end; i++) {
      btns.push(
        <button
          key={i}
          className={`imp-page-btn ${i === currentPage ? "active" : ""}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1)
        btns.push(<span key="s2" style={{ fontSize: 10, color: "#9ca3af" }}>…</span>);
      btns.push(
        <button key={totalPages} className="imp-page-btn" onClick={() => setPage(totalPages)}>
          {totalPages}
        </button>
      );
    }
    return btns;
  };

  return (
    <div className="imp-page">
      {/* Header */}
      <div className="imp-header">
        <h4>
          <FaCoins style={{ marginRight: 6, color: "#4f46e5" }} />
          IM Points – Assign &amp; Update
        </h4>
        <div style={{ display: "flex", gap: 6 }}>
          {someSelected && (
            <button
              className="imp-btn imp-btn-primary"
              onClick={() => openAssignModal("selected")}
            >
              <FaUserCheck size={11} />
              Assign to Selected ({selectedIds.length})
            </button>
          )}
          <button
            className="imp-btn imp-btn-secondary"
            onClick={() => openAssignModal("all")}
          >
            <FaUsers size={11} />
            Assign to All
          </button>
          <button
            className="imp-btn imp-btn-outline"
            onClick={() => { setPage(1); fetchUsers(); }}
            title="Refresh"
          >
            <FaSync size={10} />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="imp-toolbar">
        <form onSubmit={handleSearchSubmit} className="imp-search-form">
          <div className="imp-search-wrap">
            <FaSearch />
            <input
              className="imp-search-input"
              placeholder="Search by name, email, phone…"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <button type="submit" className="imp-btn imp-btn-primary" style={{ marginLeft: 6 }}>
            Search
          </button>
        </form>
        <div className="imp-limit-selector" style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          <span style={{ fontSize: 11, color: "#6b7280" }}>Rows per page:</span>
          <select
            className="imp-form-control"
            value={limit}
            onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
            style={{ width: "auto", padding: "4px 6px", fontSize: 11 }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            {pages.totalItems} user{pages.totalItems !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="imp-table-wrap">
        <table className="imp-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>IM Points</th>
              <th>% Deduction</th>
              <th>Expiry Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="imp-loading-row">
                  {Array.from({ length: 9 }).map((__, j) => (
                    <td key={j}><span className="imp-skeleton" /></td>
                  ))}
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr className="imp-no-data">
                <td colSpan={9}>No users found</td>
              </tr>
            ) : (
              users.map((user) => {
                const rowId = getUserRowId(user);
                const imp = user.IM_Points || {};
                const isExpired =
                  imp.expiresAt && new Date(imp.expiresAt) < new Date();
                return (
                  <tr key={rowId || user.email}>
                    <td>
                      <input
                        type="checkbox"
                        checked={rowId ? selectedIds.includes(rowId) : false}
                        onChange={() => rowId && toggleSelectOne(rowId)}
                      />
                    </td>
                    <td style={{ color: "#6b7280" }}>{user.uniqueId || "—"}</td>
                    <td>
                      {user.firstName} {user.lastName}
                    </td>
                    <td style={{ color: "#4f46e5" }}>{user.email}</td>
                    <td style={{ fontWeight: 600 }}>
                      {imp.points != null ? imp.points.toLocaleString() : "—"}
                    </td>
                    <td>{imp.percentage != null ? `${imp.percentage}%` : "—"}</td>
                    <td>
                      {imp.expiresAt
                        ? new Date(imp.expiresAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      {imp.points != null ? (
                        <span
                          className={`imp-badge ${
                            isExpired ? "imp-badge-expired" : "imp-badge-active"
                          }`}
                        >
                          {isExpired ? "Expired" : "Active"}
                        </span>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: 10 }}>None</span>
                      )}
                    </td>
                    <td>
                      <div className="imp-actions">
                        <button
                          className="imp-icon-btn"
                          title="Edit IM Points"
                          onClick={() => openAssignModal("selected", user)}
                        >
                          <FaEdit size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages.totalPages > 1 && (
        <div className="imp-pagination">
          <span>
            Page {pages.currentPage} of {pages.totalPages}
          </span>
          <div className="imp-pagination-btns">
            <button
              className="imp-page-btn"
              disabled={pages.currentPage <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹
            </button>
            {renderPageButtons()}
            <button
              className="imp-page-btn"
              disabled={pages.currentPage >= pages.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        </div>
      )}

      {/* Assign / Edit Modal */}
      {showModal && (
        <div className="imp-modal-overlay" onClick={closeModal}>
          <div className="imp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="imp-modal-header">
              <h5>
                {editUser
                  ? `Update IM Points — ${editUser.firstName} ${editUser.lastName}`
                  : form.scope === "all"
                  ? "Assign IM Points to All Users"
                  : `Assign IM Points to ${selectedIds.length} User${selectedIds.length !== 1 ? "s" : ""}`}
              </h5>
              <button className="imp-modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="imp-modal-body">
              {/* Scope toggle – only when not editing a specific user */}
              {!editUser && (
                <>
                  <div className="imp-scope-row">
                    <div
                      className={`imp-scope-option ${form.scope === "selected" ? "active" : ""}`}
                      onClick={() => handleFormChange("scope", "selected")}
                    >
                      Selected Users ({selectedIds.length})
                    </div>
                    <div
                      className={`imp-scope-option ${form.scope === "all" ? "active" : ""}`}
                      onClick={() => handleFormChange("scope", "all")}
                    >
                      All Users
                    </div>
                  </div>
                  <div
                    className={`imp-target-info ${form.scope === "all" ? "all" : ""}`}
                  >
                    {form.scope === "all"
                      ? `Points will be assigned to all ${pages.totalItems} users`
                      : `Points will be assigned to ${selectedIds.length} selected user${selectedIds.length !== 1 ? "s" : ""}`}
                  </div>
                </>
              )}

              {/* Points */}
              <div className="imp-form-row">
                <label>Points Amount *</label>
                <input
                  className="imp-form-control"
                  type="number"
                  min="0"
                  placeholder="e.g. 1000"
                  value={form.points}
                  onChange={(e) => handleFormChange("points", e.target.value)}
                />
                <div className="imp-form-hint">Total points assigned to the user</div>
              </div>

              {/* Percentage */}
              <div className="imp-form-row">
                <label>Deduction Percentage (%) *</label>
                <input
                  className="imp-form-control"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="e.g. 5"
                  value={form.percentage}
                  onChange={(e) => handleFormChange("percentage", e.target.value)}
                />
                <div className="imp-form-hint">
                  % of order subtotal deducted using IM Points at checkout
                </div>
              </div>

              {/* Expiry */}
              <div className="imp-form-row">
                <label>Expiry Period *</label>
                <select
                  className="imp-form-control"
                  value={form.expiryDays}
                  onChange={(e) => {
                    const v = e.target.value === "custom" ? "custom" : parseInt(e.target.value, 10);
                    handleFormChange("expiryDays", v);
                  }}
                >
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {form.expiryDays === "custom" && (
                  <input
                    className="imp-form-control"
                    style={{ marginTop: 5 }}
                    type="number"
                    min="1"
                    placeholder="Enter number of days"
                    value={form.customDays}
                    onChange={(e) => handleFormChange("customDays", e.target.value)}
                  />
                )}
                <div className="imp-form-hint">
                  Unused points will expire after this period
                </div>
              </div>

              {/* Admin password */}
              <div className="imp-form-row">
                <label>Admin Password *</label>
                <input
                  className="imp-form-control"
                  type="password"
                  placeholder="Enter admin password"
                  value={form.password}
                  onChange={(e) => handleFormChange("password", e.target.value)}
                />
              </div>
            </div>

            <div className="imp-modal-footer">
              <button
                className="imp-btn imp-btn-outline"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="imp-btn imp-btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting
                  ? "Saving…"
                  : editUser
                  ? "Update Points"
                  : "Assign Points"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
